import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../../api/client';
import { naviguer } from '../../App';
import { useMaPersonne } from './useMaPersonne';
import { InfosDemandeCollegue, aFra } from './DemandesCollegues';
import { ProposerRemplacementModal, PropositionEnregistree, messageErreurDemande, type ResultatProposition } from './ProposerRemplacementModal';
import { jourLocalIso } from './horaire';
import { formatDateCourt, formatDateLongue } from './format';
import { CalendrierGrille, NavigationMois } from './CalendrierGrille';
import { FicheFakOverlay } from '../fak/FicheFakOverlay';
import { grilleMois, moisDebut, moisFin, iso } from './calendrier';
import {
  type NiveauUrgence, type SeuilsUrgence, joursAvant, niveauUrgence, NIVEAU_URGENCE_INFO as NIVEAU_INFO,
} from './urgence';
import { CartesDuJour } from './CarteCalendrier';
import { DetailJour, idDetail, type DetailJourProps } from './DetailJour';
import { ConfirmationEnvoiTipee } from './GroupeAbsenceTipee';
import { elementsDuJour, type ElementJour, type Trou, type VoixCarte } from './elementsJour';
import { absenceRecoupeSeance, parseTimeRange } from './recoupement';
import { memoriserRetourCalendrier, type RetourCalendrier } from './retourCalendrier';
import { useColonneEtroite } from './useColonneEtroite';
import { useFenetreModale } from './useFenetreModale';
import type { Absence, Atelier, Demande, Meta, Personne, SeanceProgramme } from '../../types';

// Couleur d'une pastille selon le niveau d'urgence — dérivée de la classe du
// badge existant (NIVEAU_URGENCE_INFO) plutôt qu'une 2e table à maintenir en
// double : reste synchronisé automatiquement si les seuils/couleurs changent.
function couleurNiveau(niveau: NiveauUrgence): string {
  const cls = NIVEAU_INFO[niveau].badgeClass;
  if (cls.includes('badge-err')) return 'var(--err)';
  if (cls.includes('badge-warn')) return 'var(--warn)';
  if (cls.includes('badge-accent')) return 'var(--accent)';
  return 'var(--ink-3)';
}

/** « Jeudi 22 octobre 2026 » : la majuscule de la phrase seulement (pas à chaque mot). */
function majuscule(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

const BATTEMENT_PAR_DEFAUT_MIN = 60;

interface TableauCalendrierProps {
  peutGerer: boolean;
  /** Ignoré côté gestion (qui voit toujours tout) — ne s'applique qu'à un·e intervenant·e. */
  vue: 'miennes' | 'collegues';
  poleFiltre: number | '';
  meta: Meta | null;
  seuils: SeuilsUrgence;
  /** Battement de la détection Tipee (minutes) : une séance n'est « à couvrir » que si l'absence la recoupe, battement compris. */
  battementMin?: number;
  /** Retour d'une fiche ou de l'assistant ouvert depuis ce calendrier : même mois, focus sur la carte d'où l'on est parti. */
  retour?: RetourCalendrier | null;
  /** Le billet de retour est appliqué : la page ne le redonne pas si l'on rouvre le Calendrier plus tard (Liste → Calendrier). */
  onRetourApplique?: () => void;
  /** Une proposition vient d'être faite : le tableau de bord recharge « Vos remplacements ». */
  onChange?: () => void;
}

// Vue « Calendrier » du Tableau de bord — anciennement la page autonome
// Calendrier (fusionnée ici, voir docs/DECISIONS ou le fil de discussion
// Axe 2 : un seul menu, une bascule Liste/Calendrier). Trois modes :
//  - gestion (peutGerer)      : cockpit complet, diagnostic des « séances à couvrir », en CARTES dans les cases — la même vue que
//    « Mes demandes », avec en plus la personne absente et son/sa remplaçant·e (DECISIONS §27) ; les pastilles reviennent quand une colonne
//    est trop étroite (téléphone, zoom).
//  - intervenant·e « miennes »  : SES demandes et ses séances à couvrir, en CARTES dans les cases (état, progression, phrase) ;
//    les pastilles reviennent quand une colonne est trop étroite (téléphone, zoom). DECISIONS §25.
//  - intervenant·e « collègues » : demandes ouvertes des collègues (Axe 1),
//    avec « Je me propose » (récapitulatif avant de confirmer) / « Masquer », et ses
//    propres absences en repère (pour savoir si on est disponible avant de se proposer).
export function TableauCalendrier({ peutGerer, vue, poleFiltre, meta, seuils, battementMin = BATTEMENT_PAR_DEFAUT_MIN, retour, onRetourApplique, onChange }: TableauCalendrierProps) {
  const [mois, setMois] = useState(() => {
    const m = retour ? /^(\d{4})-(\d{2})$/.exec(retour.mois) : null;
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : moisDebut(new Date());
  });
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [seances, setSeances] = useState<SeanceProgramme[]>([]);
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [ateliers, setAteliers] = useState<Atelier[]>([]);
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  /** Séances (atelier_date) de mes demandes réservées à la gestion : ni montrées, ni « à couvrir ». */
  const [seancesReservees, setSeancesReservees] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  // Instantané des demandes tout juste confirmées via « Je me propose » —
  // dès la confirmation, la demande sort du périmètre « collègues » (elle
  // n'est plus en recherche) et disparaîtrait sinon du panneau sans un mot
  // d'explication. On garde un instantané pour afficher la confirmation À LA
  // PLACE, là où la personne vient de cliquer, plutôt qu'un bandeau ailleurs
  // qu'elle risque de ne pas voir.
  const [confirmationsRecentes, setConfirmationsRecentes] = useState<Map<number, Demande>>(new Map());
  // Un id par demande en cours d'action (pas un simple id partagé) : sinon
  // cliquer une case pendant qu'une autre est encore en vol réactive à tort
  // le bouton de la première.
  const [demandesEnCours, setDemandesEnCours] = useState<Set<number>>(new Set());
  const [aProposer, setAProposer] = useState<Demande | null>(null);
  const [masqueesOuvert, setMasqueesOuvert] = useState(false);
  // Date (ISO) dont le panneau de détail est ouvert — une case n'affiche
  // plus le détail des éléments du jour directement (ça débordait dès qu'un
  // jour avait plus de 2-3 éléments), juste des pastilles + un compteur ;
  // le détail complet (avec les actions) vit dans le panneau.
  // (« Mes demandes » : les cartes disent déjà l'essentiel ; le panneau complète, sur la carte cliquée.)
  const [jourOuvert, setJourOuvert] = useState<string | null>(null);
  const [cleEnAvant, setCleEnAvant] = useState<string | null>(null);
  // Envoi des brouillons « Repérée dans Tipee » depuis le détail du jour (même geste que la liste).
  const [confirmationTipee, setConfirmationTipee] = useState<Demande[] | null>(null);
  const [preparationTipee, setPreparationTipee] = useState(false);
  const [messagePanneau, setMessagePanneau] = useState<string | null>(null);

  const [trousSeuls, setTrousSeuls] = useState(false);

  const modeCollegues = !peutGerer && vue === 'collegues';
  /** « Mes demandes » de la personne (jamais la gestion, jamais l'onglet des collègues). */
  const modeMiennes = !peutGerer && !modeCollegues;
  /** Les cartes servent à la personne (« Mes demandes ») ET à la gestion : la même vue, écrite pour celle qui regarde (DECISIONS §27). */
  const parCartes = modeMiennes || peutGerer;
  const voix: VoixCarte = peutGerer ? 'gestion' : 'personne';
  const maPersonne = useMaPersonne(personnes);

  // Les cartes demandent des colonnes assez larges : sinon (téléphone, zoom à 200 %) on revient aux pastilles + détail du jour.
  const racine = useRef<HTMLDivElement>(null);
  const colonnesEtroites = useColonneEtroite(racine);
  const modeCartes = parCartes && !colonnesEtroites;

  const aujourdhui = useMemo(() => new Date(), []);
  const aujourdhuiIso = jourLocalIso(aujourdhui);
  // Les cartes (« Mes demandes », gestion) chargent la plage VISIBLE (du lundi de la 1re rangée au vendredi de la dernière) : les jours du
  // mois voisin gardent leurs cartes ; les chiffres, eux, ne comptent que le mois. Les collègues : le mois, comme avant.
  const joursGrille = useMemo(() => grilleMois(mois), [mois]);
  const du = parCartes ? iso(joursGrille[0]) : iso(moisDebut(mois));
  const au = parCartes ? iso(joursGrille[joursGrille.length - 1]) : iso(moisFin(mois));
  const moisCle = iso(mois).slice(0, 7);
  const dansLeMois = (date: string) => date.slice(0, 7) === moisCle;

  // Personnes chargées dans le même Promise.all que le reste (plutôt qu'un
  // effet séparé) : sinon `loading` retombe à false dès que séances/ateliers/
  // demandes arrivent, sans attendre la résolution de maPersonne — la grille
  // s'affiche alors avec un bouton « Je me propose » cliquable qui ne fait
  // encore rien tant que maPersonne est null.
  // Seule la réponse au DERNIER chargement compte (changement de mois, d'onglet ou action pendant l'attente) :
  // une réponse plus lente d'un chargement précédent ne doit pas écraser l'affichage actuel.
  const dernierFetch = useRef(0);
  const fetchAll = useCallback(async () => {
    const numero = ++dernierFetch.current;
    setLoading(true);
    setError(null);
    try {
      const demandesPromise = modeCollegues
        ? api.getDemandes({ du, au, vue: 'collegues' })
        : api.getDemandes(peutGerer ? { du, au } : { du, au, vue: 'miennes' });
      const personnesPromise = peutGerer ? Promise.resolve<Personne[]>([]) : api.getPersonnes({ actif: true });
      // Un échec ici ne doit pas vider le calendrier : au pire une séance réservée apparaît « à couvrir ».
      const reserveesPromise = modeMiennes ? api.getSeancesReservees({ du, au }).catch(() => []) : Promise.resolve([]);
      const [seaRes, atRes, demRes, persRes, resRes] = await Promise.all([
        api.getSeances({ du, au }),
        api.getAteliers({ actif: true, reel: true }),
        demandesPromise,
        personnesPromise,
        reserveesPromise,
      ]);
      if (numero !== dernierFetch.current) return;
      setSeances(seaRes); setAteliers(atRes); setDemandes(demRes); setPersonnes(persRes);
      setSeancesReservees(new Set(resRes.map((r) => `${r.atelier_id}_${String(r.date_atelier).slice(0, 10)}`)));
    } catch (err) {
      if (numero === dernierFetch.current) setError(err instanceof Error ? err.message : 'Échec du chargement');
    } finally {
      if (numero === dernierFetch.current) setLoading(false);
    }
  }, [du, au, peutGerer, modeCollegues, modeMiennes]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Les actions qui rechargent APRÈS une attente (synchro Tipee, fin de proposition, « Masquer ») passent par cette
  // référence, toujours à jour : la fonction capturée au moment du clic viserait l'ancien mois si l'on a changé de mois entre-temps.
  const fetchAllRef = useRef(fetchAll);
  useEffect(() => { fetchAllRef.current = fetchAll; });

  // Absences : le cockpit gestion charge tout (puis filtre côté client, comme avant) ; « Mes demandes » et l'onglet des collègues
  // n'ont besoin que des MIENNES — un repère de disponibilité, jamais les absences d'autrui.
  useEffect(() => {
    let annule = false;   // une réponse arrivée après un changement de mois ou de vue est ignorée
    const garder = (a: Absence[]) => { if (!annule) setAbsences(a); };
    if (!peutGerer) {
      if (maPersonne == null) { setAbsences([]); return; }
      api.getAbsences({ du, au, personne_id: maPersonne.id }).then(garder).catch(() => {});
    } else {
      api.getAbsences({ du, au }).then(garder).catch(() => {});
    }
    return () => { annule = true; };
  }, [du, au, peutGerer, maPersonne]);

  const titulaireParAtelier = useMemo(() => {
    const m = new Map<number, number>();
    ateliers.forEach((a) => { if (a.titulaire_personne_id != null) m.set(a.id, a.titulaire_personne_id); });
    return m;
  }, [ateliers]);

  const atelierParId = useMemo(() => {
    const m = new Map<number, Atelier>();
    ateliers.forEach((a) => m.set(a.id, a));
    return m;
  }, [ateliers]);

  const absencesScoped = useMemo(
    () => (peutGerer || vue === 'miennes') ? absences.filter((a) => peutGerer || (maPersonne != null && a.personne_id === maPersonne.id)) : absences,
    [absences, peutGerer, vue, maPersonne],
  );
  const seancesScoped = useMemo(
    () => peutGerer ? seances : seances.filter((s) => maPersonne != null && titulaireParAtelier.get(s.atelier_id) === maPersonne.id),
    [seances, peutGerer, maPersonne, titulaireParAtelier],
  );

  const seancesFiltrees = useMemo(
    () => poleFiltre === '' ? seancesScoped : seancesScoped.filter((s) => atelierParId.get(s.atelier_id)?.pole_id === poleFiltre),
    [seancesScoped, poleFiltre, atelierParId],
  );
  const demandesFiltrees = useMemo(
    () => poleFiltre === '' ? demandes : demandes.filter((d) => atelierParId.get(d.atelier_id)?.pole_id === poleFiltre),
    [demandes, poleFiltre, atelierParId],
  );

  // Les absences de chaque personne, jour par jour : la séance n'est « à couvrir » que si l'une d'elles la recoupe.
  const absencesParJourEtPersonne = useMemo(() => {
    const m = new Map<string, Absence[]>();
    absencesScoped.forEach((a) => {
      const cle = `${a.date_absence}_${a.personne_id}`;
      (m.get(cle) ?? m.set(cle, []).get(cle)!).push(a);
    });
    return m;
  }, [absencesScoped]);

  // Repère « je suis absent·e ce jour » (mes absences uniquement, déjà scopées côté serveur — voir le fetch ci-dessus).
  const mesAbsencesDates = useMemo(() => new Set(absences.map((a) => a.date_absence)), [absences]);

  // Gestion : qui est absent·e, jour par jour (une fois chaque personne, même avec plusieurs absences partielles), dans l'ordre alphabétique.
  const absentsParDate = useMemo(() => {
    const m = new Map<string, string[]>();
    if (!peutGerer) return m;
    const vus = new Set<string>();
    absencesScoped.forEach((a) => {
      const cle = `${a.date_absence}_${a.personne_id}`;
      if (vus.has(cle)) return;
      vus.add(cle);
      (m.get(a.date_absence) ?? m.set(a.date_absence, []).get(a.date_absence)!).push(a.personne_nom ?? 'Absence');
    });
    m.forEach((noms) => noms.sort((x, y) => x.localeCompare(y, 'fr')));
    return m;
  }, [absencesScoped, peutGerer]);
  /** Une absence à montrer ce jour-là : la mienne (« Mes demandes »), ou celle de n'importe qui (gestion). */
  const absentCeJour = (date: string) => peutGerer ? (absentsParDate.get(date)?.length ?? 0) > 0 : mesAbsencesDates.has(date);

  const demandesParDate = useMemo(() => {
    const m = new Map<string, Demande[]>();
    demandesFiltrees.forEach((d) => {
      const jour = String(d.date_atelier).slice(0, 10);
      (m.get(jour) ?? m.set(jour, []).get(jour)!).push(d);
    });
    return m;
  }, [demandesFiltrees]);

  // Volontairement NON filtré par pôle : une demande déjà créée couvre le
  // trou quel que soit le filtre d'affichage courant.
  const demandesParCle = useMemo(() => {
    const m = new Set<string>();
    demandes.forEach((d) => m.add(`${d.atelier_id}_${String(d.date_atelier).slice(0, 10)}`));
    return m;
  }, [demandes]);

  const couleurPole = useMemo(() => {
    const m = new Map<number, string>();
    (meta?.poles ?? []).forEach((p) => { if (p.couleur) m.set(p.id, p.couleur); });
    return m;
  }, [meta]);

  // Une séance « à couvrir » : le/la titulaire est absent·e à ce moment-là (l'absence recoupe l'horaire de la séance, battement compris —
  // la règle de la détection Tipee) ET aucune demande n'existe (ni une demande réservée à la gestion, que la personne ne voit pas).
  const trous = useMemo<Trou[]>(() => {
    if (modeCollegues) return [];
    const out: Trou[] = [];
    for (const s of seancesFiltrees) {
      const titulaireId = titulaireParAtelier.get(s.atelier_id);
      if (titulaireId == null) continue;
      const at = atelierParId.get(s.atelier_id);
      const horaire = { heure_debut: s.heure_debut ?? at?.heure_debut ?? null, heure_fin: s.heure_fin ?? at?.heure_fin ?? null };
      const sesAbsences = absencesParJourEtPersonne.get(`${s.date}_${titulaireId}`);
      if (!sesAbsences?.some((a) => absenceRecoupeSeance(parseTimeRange(a.time_range, s.date), horaire, battementMin))) continue;
      const cle = `${s.atelier_id}_${s.date}`;
      if (demandesParCle.has(cle) || seancesReservees.has(cle)) continue;
      const joursRestants = joursAvant(s.date, aujourdhui);
      out.push({
        date: s.date, atelierId: s.atelier_id, atelierNom: s.atelier_nom, poleId: at?.pole_id ?? null,
        poleCouleur: at?.pole_id != null ? couleurPole.get(at.pole_id) ?? null : null,
        lieu: s.lieu ?? at?.lieu ?? null, heureDebut: horaire.heure_debut,
        titulaireId, titulaireNom: at?.titulaire_nom ?? '—', joursRestants, niveau: niveauUrgence(joursRestants, seuils),
      });
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
  }, [modeCollegues, seancesFiltrees, titulaireParAtelier, absencesParJourEtPersonne, demandesParCle, seancesReservees, atelierParId, couleurPole, aujourdhui, seuils, battementMin]);

  const trousParDate = useMemo(() => {
    const m = new Map<string, Trou[]>();
    // « Séances à couvrir seulement » (vue de la personne) : celles qu'on peut encore couvrir, comme le chiffre — une séance passée n'en est plus une.
    const liste = modeMiennes && trousSeuls ? trous.filter((t) => t.joursRestants >= 0) : trous;
    liste.forEach((t) => { (m.get(t.date) ?? m.set(t.date, []).get(t.date)!).push(t); });
    return m;
  }, [trous, modeMiennes, trousSeuls]);

  // Les cartes : tout ce qu'une case montre, dans l'ordre des heures.
  const elementsParDate = useMemo(() => {
    const m = new Map<string, ElementJour[]>();
    if (!parCartes) return m;
    const dates = new Set([...demandesParDate.keys(), ...trousParDate.keys()]);
    dates.forEach((date) => m.set(date, elementsDuJour(trousSeuls ? [] : demandesParDate.get(date) ?? [], trousParDate.get(date) ?? [])));
    return m;
  }, [parCartes, demandesParDate, trousParDate, trousSeuls]);

  const sync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    setError(null);
    try {
      const res = await api.syncTipee();
      setSyncMsg(
        `${res.upserts} absence${res.upserts > 1 ? 's' : ''} synchronisée${res.upserts > 1 ? 's' : ''}, `
        + `${res.brouillons_crees} brouillon${res.brouillons_crees > 1 ? 's' : ''} créé${res.brouillons_crees > 1 ? 's' : ''}, `
        + `${res.tipee_confirmees} rappel${res.tipee_confirmees > 1 ? 's' : ''} Tipee confirmé${res.tipee_confirmees > 1 ? 's' : ''}`,
      );
      void fetchAllRef.current();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  // Les cartes : on quitte le calendrier pour une fiche ou l'assistant → un billet de retour (même mois, même carte).
  function memoriserRetour(cle: string | null) {
    if (parCartes) memoriserRetourCalendrier({ mois: moisCle, cle });
  }

  function creerDemandePourTrou(t: Trou) {
    memoriserRetour(`t-${t.atelierId}-${t.date}`);
    naviguer('nouvelle', undefined, { absent: String(t.titulaireId), date: t.date });
  }

  // Retour d'une fiche ou de l'assistant : le focus revient sur la carte d'où l'on est parti (une seule fois, quand le mois est chargé).
  const cleRetour = useRef(retour?.cle ?? null);
  useEffect(() => { onRetourApplique?.(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- le mois et la carte sont déjà lus (état initial, ref)
  useEffect(() => {
    if (loading || !cleRetour.current) return;
    const cle = cleRetour.current;
    cleRetour.current = null;
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-cle="${cle}"]`)?.focus());
  }, [loading]);

  function ouvrirJour(date: string, cle: string | null) {
    setMessagePanneau(null);
    setCleEnAvant(cle);
    setJourOuvert(date);
  }
  function fermerJour() { setJourOuvert(null); setCleEnAvant(null); setMessagePanneau(null); }

  // « Je me propose » ouvre la fenêtre de récapitulatif (ProposerRemplacementModal) : elle réutilise
  // saisir_remplacant + confirmer_remplacement — pas de nouvelle règle serveur, on se désigne soi-même
  // puis on confirme (les deux étapes existent pour le cas où quelqu'un d'autre désigne).
  function finProposition(d: Demande, resultat: ResultatProposition) {
    if (resultat === 'ok') {
      setConfirmationsRecentes((prev) => new Map(prev).set(d.id, d));
      setAProposer(null);
    } else if (resultat === 'partiel') {
      setAProposer(null);
      setActionMsg(`Vous êtes désigné·e pour « ${d.atelier_nom} », mais la confirmation n'a pas abouti. Confirmez-la dans « Vos remplacements », en haut de cette page.`);
    }
    // Recharge systématiquement, même en cas d'échec partiel : sinon actions_possibles reste
    // périmé côté client et retenter renvoie une erreur incompréhensible.
    void fetchAllRef.current();
    onChange?.();
  }

  async function masquer(d: Demande) {
    setDemandesEnCours((s) => new Set(s).add(d.id));
    try {
      await api.ignorerDemande(d.id);
      setDemandes((ds) => ds.map((x) => x.id === d.id ? { ...x, skippee_par_moi: true } : x));
    } catch (err) {
      setActionMsg(messageErreurDemande(err));
      void fetchAllRef.current();   // la demande a peut-être changé : le calendrier se met à jour
    } finally {
      setDemandesEnCours((s) => { const n = new Set(s); n.delete(d.id); return n; });
    }
  }

  // Brouillon repéré dans Tipee : « Envoyer ma demande de remplacement » envoie TOUTE la période d'absence, comme la carte de la liste —
  // on recharge donc les brouillons de la période (le calendrier n'en voit qu'une partie), puis la même confirmation s'ouvre.
  async function preparerEnvoiTipee(d: Demande) {
    setPreparationTipee(true);
    setMessagePanneau(null);
    try {
      const brouillons = await api.getDemandes({ vue: 'miennes', statut: 'brouillon' });
      const periode = (x: Demande) => `${x.absent_personne_id}|${x.absence_debut}`;
      const groupe = d.absence_debut ? brouillons.filter((x) => x.source === 'tipee' && x.statut === 'brouillon' && x.absence_debut && periode(x) === periode(d)) : [];
      setConfirmationTipee(groupe.length > 0 ? groupe : [d]);
    } catch (err) {
      setMessagePanneau(err instanceof Error ? err.message : "Impossible de préparer l'envoi. Réessayez.");
    } finally {
      setPreparationTipee(false);
    }
  }

  const nbMasquees = demandes.filter((d) => d.skippee_par_moi).length;

  // Chiffres du mois (« Mes demandes » charge aussi les jours voisins : ils ne comptent pas).
  const seancesDuMois = seancesScoped.filter((s) => dansLeMois(s.date)).length;
  const absencesDuMois = absencesScoped.filter((a) => dansLeMois(a.date_absence)).length;
  const trousDuMois = trous.filter((t) => dansLeMois(t.date));
  // Pour la personne : seulement celles qu'on peut encore couvrir (une séance passée sans demande n'est plus « à couvrir »).
  const nbACouvrir = modeMiennes ? trousDuMois.filter((t) => t.joursRestants >= 0).length : trousDuMois.length;

  const detail: DetailJourProps | null = parCartes && jourOuvert ? {
    voix,
    elements: elementsParDate.get(jourOuvert) ?? [],
    absent: modeMiennes && !trousSeuls && mesAbsencesDates.has(jourOuvert),
    absents: peutGerer && !trousSeuls ? absentsParDate.get(jourOuvert) ?? [] : [],
    maintenant: aujourdhui, seuils, cleEnAvant,
    onOuvrirDemande: (d) => { memoriserRetour(`d-${d.id}`); fermerJour(); naviguer('demandes', d.id); },
    onCreer: (t) => { fermerJour(); creerDemandePourTrou(t); },
    onEnvoyerTipee: (d) => { void preparerEnvoiTipee(d); },
    envoiEnCours: preparationTipee,
    message: messagePanneau,
  } : null;

  return (
    <div ref={racine}>
      <div className="toolbar mb-12">
        {!modeCollegues && (
          <label className="toggle">
            <input type="checkbox" className="toggle-input" checked={trousSeuls} onChange={(e) => setTrousSeuls(e.target.checked)} />
            <span className="toggle-slider" />
            <span className="toggle-label">Séances à couvrir seulement</span>
          </label>
        )}
        {modeCollegues && nbMasquees > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMasqueesOuvert(true)}>
            Masquées ({nbMasquees})
          </button>
        )}
        {peutGerer && (
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} disabled={syncing} onClick={sync}>
            <RefreshCw className={`icon-sm ${syncing ? 'icon-spin' : ''}`} /> Synchroniser Tipee
          </button>
        )}
      </div>

      {!modeCollegues && (
        <div className="stats-grid mb-16">
          <div className="stat-card">
            <div className="stat-value">{seancesDuMois}</div>
            <div className="stat-label">{peutGerer ? 'Séances ce mois' : 'Mes séances ce mois'}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{absencesDuMois}</div>
            <div className="stat-label">{peutGerer ? 'Absences ce mois' : 'Mes absences ce mois'}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={nbACouvrir > 0 ? { color: 'var(--msg-warn-ink, var(--warn))' } : undefined}>{nbACouvrir}</div>
            <div className="stat-label">{peutGerer ? 'Séances à couvrir' : 'Mes séances à couvrir'}</div>
          </div>
        </div>
      )}

      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}
      {syncMsg && <div className="badge badge-ok mb-12" style={{ display: 'block' }}>{syncMsg}</div>}
      {actionMsg && <div className="badge badge-accent mb-12" style={{ display: 'block' }}>{actionMsg}</div>}


      {/* Réassurance explicite quand rien n'est à couvrir ce mois-ci : la
          carte-stat « Séances à couvrir : 0 » le dit déjà en chiffre,
          mais une phrase claire lève tout doute (« ai-je raté quelque chose ? »). */}
      {!loading && !modeCollegues && nbACouvrir === 0 && (
        <div className="badge badge-ok mb-12" style={{ display: 'block' }}>
          {peutGerer
            ? 'Toutes les séances du mois sont couvertes — aucune demande à créer.'
            : "Aucune de vos séances n'est à couvrir ce mois-ci."}
        </div>
      )}

      <div className="calendrier-entete">
        <NavigationMois mois={mois} onChange={setMois} />
        <div className="calendrier-legende text-sm text-muted">
          {modeCollegues ? (
            <>
              <span><span className="text-err font-medium">Vous : absent·e</span> = vous avez une absence enregistrée ce jour-là</span>
              <span>Cliquer un jour pour le détail et vous proposer</span>
            </>
          ) : modeCartes ? (
            <>
              <span className="cal-leg"><span className="cal-leg-bord cal-leg-bord--urgent" aria-hidden="true" />Bord rouge : urgent</span>
              <span className="cal-leg"><span className="cal-leg-bord cal-leg-bord--areg" aria-hidden="true" />Bord orange : pas encore réglé</span>
              {peutGerer
                ? <span><span className="cal-absent-legende">N absent·e·s</span> = intervenant·e·s absent·e·s ce jour-là (leurs noms dans le détail du jour)</span>
                : <span><span className="cal-absent-legende">Vous : absent·e</span> = vous avez une absence enregistrée ce jour-là</span>}
              <span>Cliquez une demande pour la voir en détail, une séance à couvrir pour créer la demande</span>
            </>
          ) : modeMiennes ? (
            <>
              <span><span className="cal-absent-legende">Pastille rouge</span> = vous êtes absent·e ce jour-là</span>
              <span>Une pastille = un élément — cliquez le jour pour le détail</span>
            </>
          ) : (
            <>
              <span><span className="text-err font-medium">Nom en rouge</span> = intervenant·e absent·e ce jour-là</span>
              <span>Une pastille = un élément (couleur = urgence pour une séance à couvrir) — cliquer le jour pour le détail</span>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : modeCollegues ? (
        <CalendrierGrille mois={mois} renderJour={(_jour, dateStr) => {
          const demandesJour = demandesParDate.get(dateStr) ?? [];
          const jeSuisAbsent = mesAbsencesDates.has(dateStr);
          if (demandesJour.length === 0) return jeSuisAbsent ? <div className="text-xs text-err font-medium">Vous : absent·e</div> : null;
          return (
            <div
              role="button" tabIndex={0} data-jour={dateStr}
              className="flex flex-col gap-4 flex-1"
              style={{ cursor: 'pointer' }}
              aria-label={`${formatDateCourt(dateStr)} — ${demandesJour.length} demande${demandesJour.length > 1 ? 's' : ''}, voir le détail`}
              onClick={() => ouvrirJour(dateStr, null)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrirJour(dateStr, null); } }}
            >
              {jeSuisAbsent && <div className="text-xs text-err font-medium">Vous : absent·e</div>}
              <div className="flex gap-4 flex-wrap" aria-hidden>
                {demandesJour.map((d) => (
                  <span key={d.id} style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--accent)', opacity: d.skippee_par_moi ? 0.35 : 1,
                  }} />
                ))}
              </div>
              <div className="text-xs font-medium" style={{ color: 'var(--accent)' }} aria-hidden>
                {demandesJour.length} demande{demandesJour.length > 1 ? 's' : ''}
              </div>
            </div>
          );
        }} />
      ) : modeCartes ? (
        <CalendrierGrille
          mois={mois}
          cartes={{ aujourdhui: aujourdhuiIso, vide: (d) => (elementsParDate.get(d)?.length ?? 0) === 0 && (trousSeuls || !absentCeJour(d)) }}
          renderJour={(_jour, dateStr) => (
            <>
              {!trousSeuls && <LigneAbsents gestion={peutGerer} moi={mesAbsencesDates.has(dateStr)} noms={absentsParDate.get(dateStr)} />}
              <CartesDuJour
                elements={elementsParDate.get(dateStr) ?? []} maintenant={aujourdhui} seuils={seuils} voix={voix}
                onOuvrir={(cle, date) => ouvrirJour(date, cle)}
                onCreer={creerDemandePourTrou}
              />
            </>
          )}
        />
      ) : (
        <CalendrierGrille mois={mois} renderJour={(_jour, dateStr, _dansLeMois) => {
          const absencesJour = trousSeuls ? [] : absencesScoped.filter((a) => a.date_absence === dateStr);
          const demandesJour = trousSeuls ? [] : (demandesParDate.get(dateStr) ?? []);
          const trousJour = trousParDate.get(dateStr) ?? [];
          const total = trousJour.length + absencesJour.length + demandesJour.length;
          if (total === 0) return null;
          return (
            <div
              role="button" tabIndex={0} data-jour={dateStr}
              className="flex flex-col gap-4 flex-1"
              style={{ cursor: 'pointer' }}
              aria-label={`${formatDateCourt(dateStr)} — ${total} élément${total > 1 ? 's' : ''}, voir le détail`}
              onClick={() => ouvrirJour(dateStr, null)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrirJour(dateStr, null); } }}
            >
              <div className="flex gap-4 flex-wrap" aria-hidden>
                {trousJour.map((t) => (
                  <span key={`${t.atelierId}_${t.date}`} title={NIVEAU_INFO[t.niveau].label}
                    style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: couleurNiveau(t.niveau) }} />
                ))}
                {absencesJour.map((a) => (
                  <span key={a.id} title="Absence" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: 'var(--err)', opacity: 0.7 }} />
                ))}
                {demandesJour.map((d) => (
                  <span key={d.id} title={d.atelier_nom} style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: 'var(--accent)' }} />
                ))}
              </div>
              <div className="text-xs font-medium text-ink-2" aria-hidden>{total} élément{total > 1 ? 's' : ''}</div>
            </div>
          );
        }} />
      )}

      {jourOuvert && (
        <PanneauJour
          dateStr={jourOuvert}
          onFermer={fermerJour}
          modeCollegues={modeCollegues}
          detail={detail}
          demandesJour={modeCollegues ? demandesParDate.get(jourOuvert) ?? [] : []}
          confirmationsJour={[...confirmationsRecentes.values()].filter((d) => d.date_atelier === jourOuvert)}
          jeSuisAbsent={mesAbsencesDates.has(jourOuvert)}
          enCours={demandesEnCours}
          peutSePoser={maPersonne != null}
          aujourdhui={aujourdhuiIso}
          onProposer={(d) => { setActionMsg(null); setAProposer(d); }}
          onMasquer={masquer}
          fenetreAuDessus={aProposer != null || confirmationTipee != null}
        />
      )}

      {/* Après le panneau dans le DOM (même z-index) : la fenêtre passe au-dessus. */}
      {aProposer && maPersonne != null && (
        <ProposerRemplacementModal
          demande={aProposer} maPersonneId={maPersonne.id}
          onClose={() => setAProposer(null)}
          onFini={(r) => finProposition(aProposer, r)}
        />
      )}

      {confirmationTipee && (
        <ConfirmationEnvoiTipee
          demandes={confirmationTipee}
          onFermer={() => setConfirmationTipee(null)}
          onChange={() => { void fetchAllRef.current(); onChange?.(); }}
        />
      )}

      {masqueesOuvert && (
        <PanneauMasquees
          onFermer={() => setMasqueesOuvert(false)}
          onReafficher={async (id) => {
            await api.reafficherDemande(id);
            setDemandes((ds) => ds.map((x) => x.id === id ? { ...x, skippee_par_moi: false } : x));
          }}
        />
      )}
    </div>
  );
}

/** En haut d'une case : « Vous : absent·e » (« Mes demandes ») ou « 3 absent·e·s » (gestion : les noms au survol et dans le détail du jour). */
function LigneAbsents({ gestion, moi, noms }: { gestion: boolean; moi: boolean; noms?: string[] }) {
  if (!gestion) return moi ? <div className="cal-absent">Vous : absent·e</div> : null;
  if (!noms || noms.length === 0) return null;
  return <div className="cal-absent" title={noms.join(', ')}>{noms.length} absent·e{noms.length > 1 ? '·s' : ''}<span className="cal-sr">{` : ${noms.join(', ')}`}</span></div>;
}

// Détail complet d'un jour, ouvert au clic depuis une case de la grille —
// les cases elles-mêmes ne montrent plus que des pastilles + un compteur
// (une case pouvait déborder dès que 3-4 éléments tombaient le même jour),
// sauf en cartes (« Mes demandes », gestion), où le détail complète la carte cliquée.
// Une fenêtre au sens strict : le focus y entre, Tab y reste, Échap la ferme et le focus revient où il était.
function PanneauJour({
  dateStr, onFermer, modeCollegues, detail, demandesJour, confirmationsJour, jeSuisAbsent, enCours, peutSePoser, aujourdhui, onProposer, onMasquer, fenetreAuDessus,
}: {
  dateStr: string;
  onFermer: () => void;
  modeCollegues: boolean;
  /** « Mes demandes » et gestion : le détail du jour en cartes. L'onglet des collègues a le sien (demandesJour). */
  detail: DetailJourProps | null;
  /** Collègues : les demandes ouvertes ce jour-là. */
  demandesJour: Demande[];
  /** Demandes tout juste confirmées via « Je me propose » ce jour-là — ne
   *  sont plus dans demandesJour (statut sorti du périmètre collègues), on
   *  les affiche quand même pour que la confirmation reste visible à la
   *  place, plutôt qu'une disparition silencieuse. */
  confirmationsJour: Demande[];
  jeSuisAbsent: boolean;
  enCours: Set<number>;
  peutSePoser: boolean;
  /** Jour local au format AAAA-MM-JJ : une séance passée ne peut plus recevoir de proposition. */
  aujourdhui: string;
  onProposer: (d: Demande) => void;
  onMasquer: (d: Demande) => void;
  /** Une fenêtre (proposition, confirmation d'envoi) est ouverte par-dessus le détail : elle garde le clavier. */
  fenetreAuDessus: boolean;
}) {
  // Fermer le panneau avant toute navigation (fiche demande, assistant
  // « nouvelle demande ») : sinon jourOuvert reste posé et le panneau peut
  // réapparaître si l'utilisateur revient en arrière sur le Tableau de bord.
  function allerVoirDemande(id: number) { onFermer(); naviguer('demandes', id); }

  // FRA consultable SANS quitter le panneau (aperçu par-dessus) — c'est
  // l'étape « Consulter la FRA » du BPMN, avant de décider de se proposer.
  const [ficheFak, setFicheFak] = useState<{ atelierId: number; demandeId: number } | null>(null);
  // Une fiche existe et vaut le coup d'œil dès qu'elle a été rédigée —
  // brouillon compris (même logique que la lecture : le contenu prime sur le
  // statut) ; « manquant »/« archivé » = rien à montrer.
  const aFiche = aFra;
  // Le focus entre sur la demande cliquée (« Mes demandes »), sinon sur « Fermer ».
  // La fiche FRA, ouverte par-dessus, est une fenêtre aussi : tant qu'une fenêtre est au-dessus, le détail du jour ne prend ni Échap ni Tab.
  const dessus = useRef(false);
  dessus.current = fenetreAuDessus || ficheFak != null;
  const { ref } = useFenetreModale(onFermer, {
    focusInitial: () => (detail?.cleEnAvant ? document.getElementById(idDetail(detail.cleEnAvant)) : null),
    auDessus: () => dessus.current,
  });

  return (
    <div className="panel-overlay open">
      <div className="panel-overlay-bg" onClick={onFermer} />
      <div className="panel-slide open" role="dialog" aria-modal="true" aria-labelledby="cal-pt" ref={ref}>
        <div className="panel-slide-head">
          <h2 className="panel-slide-title cal-pt" id="cal-pt">{majuscule(formatDateLongue(dateStr))}</h2>
          <button className="panel-slide-close" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="panel-slide-body">
          {detail ? (
            <DetailJour {...detail} />
          ) : modeCollegues ? (
            demandesJour.length === 0 && confirmationsJour.length === 0 && !jeSuisAbsent ? (
              <p className="text-sm text-ink-3" style={{ margin: 0 }}>Rien ce jour-là.</p>
            ) : (
              <div className="flex flex-col gap-12">
                {jeSuisAbsent && <div className="text-sm text-err font-medium">Vous : absent·e</div>}
                {confirmationsJour.map((d) => (
                  <PropositionEnregistree
                    key={`confirme-${d.id}`} demande={d}
                    onVoir={() => allerVoirDemande(d.id)}
                    onFra={aFiche(d) ? () => setFicheFak({ atelierId: d.atelier_id, demandeId: d.id }) : undefined}
                  />
                ))}
                {demandesJour.map((d) => {
                  const passee = String(d.date_atelier).slice(0, 10) < aujourdhui;
                  const peutProposer = !passee && (d.actions_possibles ?? []).includes('saisir_remplacant');
                  const actif = enCours.has(d.id);
                  return (
                    <div key={d.id} style={{ opacity: d.skippee_par_moi ? 0.5 : 1, borderBottom: '1px solid var(--line)', paddingBottom: 12, display: 'grid', gap: 6 }}>
                      <InfosDemandeCollegue
                        d={d} marquerVotrePole
                        onOuvrir={() => allerVoirDemande(d.id)}
                        onFra={() => setFicheFak({ atelierId: d.atelier_id, demandeId: d.id })}
                      />
                      <div className="flex gap-8 mt-4" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                        {peutProposer && (
                          <button type="button" className="btn btn-sm btn-accent" disabled={actif || !peutSePoser} onClick={() => onProposer(d)}>
                            Je me propose
                          </button>
                        )}
                        {passee && <span className="text-sm text-ink-3">Séance passée</span>}
                        {!d.skippee_par_moi && (
                          <button type="button" className="btn btn-sm btn-ghost" disabled={actif} onClick={() => onMasquer(d)}>Masquer</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : null}
        </div>
      </div>
      {/* Rendu APRÈS le panel-slide (même z-index 200 : l'ordre DOM fait
          passer la fiche au-dessus) — le panneau reste ouvert derrière,
          on revient exactement où on était en fermant la fiche. */}
      {ficheFak && (
        <FicheFakOverlay atelierId={ficheFak.atelierId} demandeId={ficheFak.demandeId} onClose={() => setFicheFak(null)} />
      )}
    </div>
  );
}

function PanneauMasquees({ onFermer, onReafficher }: { onFermer: () => void; onReafficher: (id: number) => Promise<void> }) {
  const [items, setItems] = useState<Demande[] | null>(null);
  const [enCours, setEnCours] = useState<number | null>(null);
  useEffect(() => { api.getDemandesIgnorees().then(setItems).catch(() => setItems([])); }, []);

  async function reafficher(id: number) {
    setEnCours(id);
    try {
      await onReafficher(id);
      setItems((its) => its?.filter((d) => d.id !== id) ?? its);
    } finally {
      setEnCours(null);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Demandes masquées</h3>
          <button className="close-btn" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          {items == null ? (
            <div className="spinner" />
          ) : items.length === 0 ? (
            <p className="text-sm text-ink-2" style={{ margin: 0 }}>Aucune demande masquée.</p>
          ) : (
            <div className="flex flex-col gap-8">
              {items.map((d) => (
                <div key={d.id} className="flex items-center gap-8" style={{ justifyContent: 'space-between' }}>
                  <span className="text-sm">{formatDateCourt(d.date_atelier)} · {d.atelier_nom}{d.atelier_lieu ? ` (${d.atelier_lieu})` : ''} · {d.absent_nom}</span>
                  <button type="button" className="btn btn-sm btn-ghost" disabled={enCours === d.id} onClick={() => reafficher(d.id)}>
                    {enCours === d.id ? '…' : 'Réafficher'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-sm btn-ghost" onClick={onFermer}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
